import { FeedbackForm } from "../components/common/FeedbackForm.jsx";

export function Help() {
   return (
      <div className="p-6 max-w-3xl space-y-10">
         <div>
            <h1 className="text-2xl font-bold text-slate-900">Help & Guide</h1>
            <p className="mt-1 text-sm text-slate-500">
               Everything you need to know about using Launchpad.
            </p>
         </div>

         {/* Quick Start */}
         <div className="rounded-lg border-2 border-primary-200 bg-primary-50 p-5">
            <h2 className="text-sm font-bold text-primary-700 mb-3">⚡ Quick Start — 3 steps to get going</h2>
            <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-4">
               <li><strong>Add your targets</strong> — on the Board, click <strong>+ New</strong> to add companies you want to network into (Networking tab) or jobs you've applied to (Applications tab)</li>
               <li><strong>Set a weekly goal</strong> — go to Settings → Goals and adopt a preset like "Send 5 outreach messages" or "Apply to 3 jobs this week"</li>
               <li><strong>Share with your coach</strong> — go to Settings → Generate Share Link and send the URL to your mentor or accountability partner</li>
            </ol>
            <p className="text-xs text-slate-500 mt-3">That's it — your Dashboard will now tell you what to do each day. Keep reading below for the full guide.</p>
         </div>

         {/* Board overview */}
         <Section title="The Board" icon="📋">
            <p>
               Your main view — a Kanban board with two modes: <strong>Networking</strong> (primary) and <strong>Applications</strong>. Toggle between them using the tabs next to the "Board" title.
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">Both boards share:</h4>
            <ul>
               <li>
                  <strong>Drag & Drop</strong> — between columns changes status,
                  within a column reorders
               </li>
               <li>
                  <strong>Quick Status Change</strong> — right-click (desktop) or
                  long-press (mobile) for a status dropdown
               </li>
               <li>
                  <strong>Board/List toggle</strong> — switch views in the header
               </li>
               <li>
                  <strong>Search & filter</strong> — scoped to the active board
               </li>
            </ul>

            <h4 className="font-semibold text-slate-800 mt-4">Cards show:</h4>
            <ul>
               <li>
                  <strong>Company & Role</strong> (🤝 icon on networking cards)
               </li>
               <li>
                  <strong>Priority</strong> — colored left border (red = high,
                  amber = medium, green = low)
               </li>
               <li>
                  <strong>Tags</strong> — up to 3 visible
               </li>
               <li>
                  <strong>Activity line</strong> — e.g. "→ Follow up with
                  recruiter (today)" or "Applied 2d ago"
               </li>
               <li>
                  <strong>Last Contacted</strong> — "✉ Contacted 3d ago" always
                  visible when set
               </li>
               <li>
                  <strong>Stale nudge</strong> — "⏸ No update in X weeks"
                  on cards with no status change for 14+ days
               </li>
               <li>
                  <strong>Notes preview</strong> — hover a card to see the first
                  2 lines (desktop only)
               </li>
            </ul>

            <h4 className="font-semibold text-slate-800 mt-4">Card colors</h4>
            <ul>
               <li>
                  <strong>Red background</strong> — overdue next action
               </li>
               <li>
                  <strong>Amber background</strong> — stale (14+ days)
               </li>
               <li>
                  <strong>White background</strong> — healthy
               </li>
            </ul>
         </Section>

         {/* Networking */}
         <Section title="Networking Pipeline" icon="🤝">
            <p>
               The primary board for tracking outreach and relationship-building. Five stages:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
               {[
                  ["Target", "bg-slate-400", "Companies you want to reach"],
                  ["Researched", "bg-blue-400", "Done your homework"],
                  ["Outreach Sent", "bg-amber-400", "Message / email sent"],
                  ["Chat Scheduled", "bg-emerald-400", "Meeting booked"],
                  ["Nurturing", "bg-purple-400", "Ongoing relationship"],
               ].map(([name, color, desc]) => (
                  <div
                     key={name}
                     className="rounded border border-slate-200 p-2 text-center"
                  >
                     <div
                        className={`mx-auto size-3 rounded-full ${color} mb-1`}
                     />
                     <p className="text-xs font-semibold text-slate-700">
                        {name}
                     </p>
                     <p className="text-[10px] text-slate-500">{desc}</p>
                  </div>
               ))}
            </div>

            <h4 className="font-semibold text-slate-800 mt-4">Creating a networking card</h4>
            <p>
               Click <Kbd>+ New</Kbd> → enter a Company name → Create. Role defaults to "TBD" and application fields stay hidden until needed.
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">
               Convert to Application
            </h4>
            <p>
               When a networking contact leads to a real opportunity, open the
               card and click <strong>Convert to Application</strong>. This
               moves it to the Applications board with status "Applied" and
               reveals all application fields for editing.
            </p>
         </Section>

         {/* Applications Board */}
         <Section title="Applications Board" icon="📄">
            <p>
               Track formal job applications. Switch via the <strong>Applications</strong> tab next to the Board title.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
               {[
                  ["Saved", "bg-status-saved", "Interested, not yet applied"],
                  ["Applied", "bg-status-applied", "Application submitted"],
                  ["Interviewing", "bg-status-interviewing", "Active interviews"],
                  ["Offer", "bg-status-offer", "Received an offer"],
                  ["Rejected", "bg-status-rejected", "Closed / archived"],
               ].map(([name, color, desc]) => (
                  <div
                     key={name}
                     className="rounded border border-slate-200 p-2 text-center"
                  >
                     <div className={`mx-auto size-3 rounded-full ${color} mb-1`} />
                     <p className="text-xs font-semibold text-slate-700">{name}</p>
                     <p className="text-[10px] text-slate-500">{desc}</p>
                  </div>
               ))}
            </div>
            <Tip>
               Most applications come from networking. Use "Convert to Application" when a relationship turns into an opportunity.
            </Tip>
         </Section>

         {/* Creating */}
         <Section title="Creating a Card" icon="+">
            <h4 className="font-semibold text-slate-800">Networking card</h4>
            <ol>
               <li>Make sure you're on the <strong>Networking</strong> tab</li>
               <li>Click <Kbd>+ New</Kbd></li>
               <li>Enter <strong>Company</strong> name (that's all that's needed)</li>
               <li>Optionally add: Contact, Next Action + Date, Last Contacted, Tags, Notes</li>
               <li>Click <Kbd>Create</Kbd></li>
            </ol>

            <h4 className="font-semibold text-slate-800 mt-4">Application card</h4>
            <ol>
               <li>Switch to the <strong>Applications</strong> tab</li>
               <li>Click <Kbd>+ New</Kbd></li>
               <li>Fill in <strong>Company</strong> and <strong>Role</strong> (required)</li>
               <li>Optionally add: Date Applied, Location, Job URL, Salary, Priority, Source, Contact, Next Action + Date, Tags, Notes, Job Description</li>
               <li>Click <Kbd>Create</Kbd></li>
            </ol>
            <Tip>
               If a matching Company + Role already exists, you'll see a
               duplicate warning. You can still proceed.
            </Tip>
         </Section>

         {/* Editing */}
         <Section title="Editing a Card" icon="✎">
            <p>Click any card to open it. The modal title shows "Edit Company" (networking) or "Edit Application" (applications). It has 4 tabs:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
               {[
                  ["Details", "All fields, checklist, debrief"],
                  ["Timeline", "Status change history"],
                  ["Notes", "Text editor for tracking notes"],
                  ["Job Description", "Full job description text"],
               ].map(([tab, desc]) => (
                  <div
                     key={tab}
                     className="rounded border border-slate-200 px-3 py-2"
                  >
                     <p className="text-sm font-medium text-slate-800">{tab}</p>
                     <p className="text-xs text-slate-500">{desc}</p>
                  </div>
               ))}
            </div>

            <h4 className="font-semibold text-slate-800 mt-4">Checklist</h4>
            <p>
               Add interview prep items, check them off, or apply a saved
               template.
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">Debrief</h4>
            <p>
               Visible when status is Interviewing (or was). Record per round:
            </p>
            <ul>
               <li>Interview date</li>
               <li>What went well?</li>
               <li>What caught me off guard?</li>
               <li>What do I need to follow up on?</li>
            </ul>

            <h4 className="font-semibold text-slate-800 mt-4">Deleting</h4>
            <p>
               Red <strong>Delete</strong> button (bottom-left) → confirmation
               dialog. <strong>Permanent and irreversible.</strong>
            </p>
         </Section>

         {/* Search & Filter */}
         <Section title="Search & Filter" icon="⌕">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
               {[
                  [
                     "Search bar",
                     "Matches company, role, notes (300ms debounce)",
                  ],
                  ["Status buttons", "Multi-select toggle"],
                  ["Priority buttons", "Multi-select toggle"],
                  ["Tags dropdown", "Filter by any tag"],
                  ["Date range", "From / to date pickers"],
                  ["Clear", "Resets all active filters"],
               ].map(([label, desc]) => (
                  <div key={label} className="flex gap-2 items-start">
                     <span className="text-primary-500 text-xs mt-0.5">●</span>
                     <div>
                        <p className="text-sm font-medium text-slate-700">
                           {label}
                        </p>
                        <p className="text-xs text-slate-500">{desc}</p>
                     </div>
                  </div>
               ))}
            </div>
            <Tip>
               Both Board and List views respect active filters. A match count
               badge appears when filtering.
            </Tip>
         </Section>

         {/* Dashboard */}
         <Section title="Dashboard" icon="🧭">
            <p className="text-sm text-slate-600 mb-2">
               Your landing page after sign-in. Shows what needs attention
               today.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
               {[
                  [
                     "Metrics Cards",
                     "Outreach This Week, Conversations, Active Apps, Due Today",
                  ],
                  [
                     "Goals",
                     "Weekly progress bars with streaks, last period summary, and +1 for custom goals",
                  ],
                  [
                     "Today's Actions",
                     "Stale relationships, due/overdue items, missing next steps — click to open",
                  ],
                  [
                     "Networking Pipeline",
                     "Visual funnel: Target → Outreach → Scheduled → Nurturing",
                  ],
                  [
                     "Weekly Applications",
                     "12-week chart with goal line indicator",
                  ],
                  [
                     "Applications",
                     "Compact status breakdown with interview rate",
                  ],
               ].map(([label, desc]) => (
                  <div
                     key={label}
                     className="rounded border border-slate-200 px-3 py-2"
                  >
                     <p className="text-sm font-medium text-slate-800">
                        {label}
                     </p>
                     <p className="text-xs text-slate-500">{desc}</p>
                  </div>
               ))}
            </div>
            <Tip>
               The sidebar badge shows how many apps have overdue next actions.
            </Tip>
         </Section>

         {/* Salary */}
         <Section title="Salary Comparison" icon="$">
            <p>
               Shows applications with status <strong>Offer</strong>{" "}
               side-by-side.
            </p>
            <ol>
               <li>Check which offers to compare</li>
               <li>View original and normalized annual salary</li>
            </ol>
            <p className="text-xs text-slate-500 mt-2">
               Normalization: hourly ×2,080 · weekly ×52 · monthly ×12 · annual
               ×1
            </p>
         </Section>

         {/* Import */}
         {/* <Section title="Import" icon="↑">
        <ol>
          <li>Open your spreadsheet → select all rows <strong>including the header</strong></li>
          <li>Copy (Cmd+C)</li>
          <li>Paste into the Import page text area</li>
          <li>Click <Kbd>Preview</Kbd> → verify row count and mapping</li>
          <li>Click <Kbd>Import</Kbd></li>
        </ol>
      </Section> */}

         {/* Goals */}
         <Section title="Goals & Progress" icon="🎯">
            <p>
               Set measurable targets for your job search and track progress on
               your Dashboard.
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">
               Creating Goals
            </h4>
            <p>
               Go to <strong>Settings → Goals</strong>. Choose a preset or click{" "}
               <Kbd>+ New Goal</Kbd> to create your own with a title, metric,
               target number, and period (weekly/monthly/total).
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">
               Auto-tracked Metrics
            </h4>
            <ul>
               <li>
                  <strong>Applications sent</strong> — counts apps that move to
                  Applied this period
               </li>
               <li>
                  <strong>Outreach messages sent</strong> — networking cards
                  reaching Outreach
               </li>
               <li>
                  <strong>Conversations completed</strong> — networking cards
                  reaching Chat Scheduled or Nurturing
               </li>
               <li>
                  <strong>Interviews scheduled</strong> — apps reaching
                  Interviewing
               </li>
               <li>
                  <strong>Custom</strong> — manually increment with the +1
                  button on Dashboard
               </li>
            </ul>

            <h4 className="font-semibold text-slate-800 mt-4">
               Dashboard Card
            </h4>
            <ul>
               <li>
                  Progress bars show <strong>current/target</strong> for each
                  active goal
               </li>
               <li>
                  🔥 <strong>Streak badge</strong> — consecutive periods where
                  you hit the goal
               </li>
               <li>
                  <strong>Last period summary</strong> — "Last week: 8/10 ✓" for
                  context
               </li>
               <li>
                  <strong>Click a goal</strong> to jump to the Board filtered to
                  relevant cards
               </li>
               <li>
                  🎉 <strong>Celebration toast</strong> when you complete a goal
                  during your session
               </li>
            </ul>
            <Tip>
               Weekly goals reset every Monday. Start with the suggested presets
               and adjust targets as you find your rhythm.
            </Tip>
         </Section>

         {/* Practice */}
         <Section title="Practice Stories" icon="🎯">
            <p>
               Prepare for behavioral interviews using structured
               Problem-Action-Result format stories (Problem → Action → Result).
            </p>
            <h4 className="font-semibold text-slate-800 mt-4">
               Creating a Story
            </h4>
            <ol>
               <li>
                  Click <Kbd>+ New Story</Kbd>
               </li>
               <li>Add a title, role/company context, and year</li>
               <li>
                  Fill in 3 acts (default: Problem, Action, Result) — each with
                  up to 3 bullet points and sub-details
               </li>
               <li>Save</li>
            </ol>

            <h4 className="font-semibold text-slate-800 mt-4">Practice Mode</h4>
            <ol>
               <li>
                  Click <Kbd>Practice</Kbd> on any story
               </li>
               <li>
                  Try to recite from memory (a timer tracks you — aim for under
                  3 minutes)
               </li>
               <li>Reveal bullet points one-by-one to check yourself</li>
               <li>Score yourself 1–5 when done</li>
            </ol>
            <Tip>
               Stories due for practice are highlighted with an amber badge. The
               system uses spaced repetition — practice consistently to build
               long-term recall.
            </Tip>
         </Section>

         {/* Sharing */}
         <Section title="Sharing" icon="🔗">
            <p>
               Generate a read-only link to share your progress with mentors,
               career coaches, or accountability partners.
            </p>
            <ol>
               <li>
                  Go to <strong>Settings</strong>
               </li>
               <li>
                  Click <Kbd>Generate Share Link</Kbd>
               </li>
               <li>Copy the URL and send it</li>
            </ol>
            <p>
               The recipient sees: networking pipeline, goal progress,
               application funnel, weekly activity trend, this week's moves, and
               items needing attention — without access to company names, notes,
               or contacts. Revoke anytime from Settings.
            </p>
         </Section>

         {/* Quick Clip */}
         <Section title="Quick Clip" icon="⚡">
            <p>A lightweight page for saving jobs fast:</p>
            <ol>
               <li>
                  Bookmark{" "}
                  <a href="/quick-clip" className="text-primary-600 underline">
                     /quick-clip
                  </a>
               </li>
               <li>Enter Company + Role (+ optional URL)</li>
               <li>
                  Click <Kbd>Clip It</Kbd>
               </li>
            </ol>
            <p className="text-xs text-slate-500 mt-1">
               Appears on your board within 60 seconds via background sync.
            </p>
         </Section>

         {/* Export */}
         <Section title="CSV Export" icon="↓">
            <p>
               Go to <strong>Settings</strong> and click{" "}
               <strong>Export CSV</strong>. Downloads all your applications as a
               CSV file. Opens in Excel/Google Sheets.
            </p>
         </Section>

         {/* Settings */}
         <Section title="Settings" icon="⚙">
            <h4 className="font-semibold text-slate-800">Goals</h4>
            <p>
               Create, edit, and manage weekly/monthly goals. Suggested presets appear when you have no goals yet.
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">
               Checklist Templates
            </h4>
            <p>
               Create reusable checklists (e.g. "Interview Prep", "Offer
               Evaluation") and apply them to any application's checklist panel
               with one click.
            </p>

            <h4 className="font-semibold text-slate-800 mt-4">Display Name</h4>
            <p>Shown on your shared link. Set it once under Profile.</p>

            <h4 className="font-semibold text-slate-800 mt-4">Show Welcome Guide Again</h4>
            <p>Re-triggers the onboarding modal on your next Dashboard visit.</p>

            <h4 className="font-semibold text-slate-800 mt-4">
               Delete Account
            </h4>
            <p>
               Permanently removes all your data. <strong>Irreversible</strong>{" "}
               — requires confirmation.
            </p>
         </Section>

         {/* Dark Mode */}
         <Section title="Dark Mode" icon="☾">
            <p>
               Toggle via the sidebar button. Preference is saved across
               sessions — no flash on reload.
            </p>
         </Section>

         {/* Keyboard */}
         <Section title="Keyboard Shortcuts" icon="⌨">
            <h4 className="font-semibold text-slate-800">
               Global (Board page)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
               {[
                  ["New card (company or application)", "n"],
                  ["Focus search", "/"],
                  ["Toggle board/list", "v"],
                  ["Show shortcuts help", "?"],
                  ["Close modal / blur", "Esc"],
               ].map(([action, key]) => (
                  <div
                     key={action}
                     className="flex justify-between py-1 border-b border-slate-100"
                  >
                     <span className="text-sm text-slate-700">{action}</span>
                     <kbd className="text-xs bg-slate-100 rounded px-1.5 py-0.5 font-mono text-slate-600">
                        {key}
                     </kbd>
                  </div>
               ))}
            </div>
            <h4 className="font-semibold text-slate-800 mt-4">Drag & Drop</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
               {[
                  ["Pick up card", "Space / Enter"],
                  ["Move card", "Arrow keys"],
                  ["Drop card", "Space / Enter"],
               ].map(([action, key]) => (
                  <div
                     key={action}
                     className="flex justify-between py-1 border-b border-slate-100"
                  >
                     <span className="text-sm text-slate-700">{action}</span>
                     <kbd className="text-xs bg-slate-100 rounded px-1.5 py-0.5 font-mono text-slate-600">
                        {key}
                     </kbd>
                  </div>
               ))}
            </div>
            <Tip>
               Shortcuts are disabled while typing in inputs. Press <Kbd>?</Kbd>{" "}
               on the Board for a quick reference overlay.
            </Tip>
         </Section>

         {/* Tips */}
         <Section title="Pro Tips" icon="💡">
            <ul>
               <li>
                  <strong>Set Next Action + Date</strong> on every active app —
                  drives reminders and card activity text
               </li>
               <li>
                  <strong>Use tags</strong> to categorize (remote, senior,
                  referral) — they're filterable
               </li>
               <li>
                  <strong>Drag to Rejected</strong> to archive — records the
                  change in timeline
               </li>
               <li>
                  <strong>Dashboard is your home base</strong> — it tells you
                  what to do today every time you sign in
               </li>
               <li>
                  <strong>Export before interviews</strong> — CSV gives you a
                  quick reference sheet
               </li>
               <li>
                  <strong>Install on mobile</strong> — use your browser's "Add
                  to Home Screen" for an app-like experience
               </li>
               <li>
                  <strong>Start with networking</strong> — add your target
                  companies first. Applications come from relationships, not cold
                  apps.
               </li>
               <li>
                  <strong>One card per contact</strong> — start with a company
                  card in Target. When you identify people to reach out to,
                  use <strong>Duplicate</strong> to create a card per contact
                  (rename to "Name - Company"). Each person's follow-up cadence
                  and next action date track independently.
               </li>
               <li>
                  <strong>Practice stories regularly</strong> — spaced
                  repetition builds recall. Aim for under 3 minutes per story.
               </li>
               <li>
                  <strong>Share your link</strong> — give your mentor or
                  accountability partner your share URL so they can see your
                  progress.
               </li>
               <li>
                  <strong>Create checklist templates</strong> — build one
                  "Interview Prep" template in Settings and apply it to every
                  new interview with one click.
               </li>
               <li>
                  <strong>Use Last Contacted</strong> — set it on networking
                  cards to get stale-relationship reminders on the Dashboard.
               </li>
               <li>
                  <strong>Set weekly goals</strong> — even a modest "apply to 3
                  jobs this week" keeps momentum visible and builds streaks.
               </li>
            </ul>
         </Section>

         {/* Feedback */}
         <Section title="Feedback" icon="💬">
            <p className="mb-3">
               Have an idea, found a bug, or just want to share what's working? We'd love to hear from you.
            </p>
            <FeedbackForm />
         </Section>
      </div>
   );
}

function Section({ title, icon, children }) {
   return (
      <section className="space-y-3">
         <div className="flex items-center gap-2">
            <span className="flex items-center justify-center size-7 rounded bg-primary-100 text-primary-700 text-sm font-bold">
               {icon}
            </span>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
         </div>
         <div className="pl-9 space-y-2 text-sm text-slate-600 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:space-y-1 [&_strong]:text-slate-800">
            {children}
         </div>
      </section>
   );
}

function Tip({ children }) {
   return (
      <div className="mt-3 rounded bg-accent-50 border border-accent-200 px-3 py-2 text-xs text-accent-700">
         💡 {children}
      </div>
   );
}

function Kbd({ children }) {
   return (
      <kbd className="rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-xs font-mono font-medium text-slate-700">
         {children}
      </kbd>
   );
}
